import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

export default function AssociationManager() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) return <p className="text-sm text-stone-500">Chargement...</p>;

  const bureau = form.bureau || [];

  const setMember = (index, key, value) => {
    const next = bureau.map((m, i) => (i === index ? { ...m, [key]: value } : m));
    setForm({ ...form, bureau: next });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        assoc_presentation: form.assoc_presentation,
        assoc_adhesion: form.assoc_adhesion,
        bureau: bureau.filter((m) => m.name || m.role),
      });
      toast.success("Page « L'Association » mise à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="association-manager" className="max-w-3xl">
      <form onSubmit={submit} className="space-y-6">
        <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          <h3 className="font-display text-lg font-bold uppercase text-[#1E1B18]">Présentation de l'association</h3>
          <div>
            <label className={labelClass}>Texte « Qui sommes-nous »</label>
            <textarea data-testid="assoc-presentation-input" rows="6" className={inputClass} value={form.assoc_presentation || ""} onChange={(e) => setForm({ ...form, assoc_presentation: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Texte « Adhésion »</label>
            <textarea data-testid="assoc-adhesion-input" rows="4" className={inputClass} value={form.assoc_adhesion || ""} onChange={(e) => setForm({ ...form, assoc_adhesion: e.target.value })} />
          </div>
        </div>

        <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          <h3 className="font-display text-lg font-bold uppercase text-[#1E1B18]">Le bureau</h3>
          <p className="text-xs text-stone-500">Ajoutez les membres du bureau (ils s'affichent sur la page L'Association). Cochez « Directeur de la publication » pour la personne dont le nom doit apparaître dans les mentions légales.</p>
          {bureau.map((m, i) => (
            <div key={i} className="rounded-lg border border-stone-200 p-3" data-testid={`bureau-row-${i}`}>
              <div className="flex items-center gap-3">
                <input className={inputClass} placeholder="Rôle (ex : Présidente)" value={m.role || ""} onChange={(e) => setMember(i, "role", e.target.value)} data-testid={`bureau-role-${i}`} />
                <input className={inputClass} placeholder="Nom (ex : Marie D.)" value={m.name || ""} onChange={(e) => setMember(i, "name", e.target.value)} data-testid={`bureau-name-${i}`} />
                <button
                  type="button"
                  data-testid={`bureau-remove-${i}`}
                  onClick={() => setForm({ ...form, bureau: bureau.filter((_, j) => j !== i) })}
                  className="rounded bg-red-50 p-2.5 text-red-700 hover:bg-red-100"
                  aria-label="Retirer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <label className="mt-2 flex items-center gap-2 text-xs text-stone-600">
                <input
                  type="checkbox"
                  data-testid={`bureau-director-${i}`}
                  checked={!!m.directeur_publication}
                  onChange={(e) => setForm({ ...form, bureau: bureau.map((mm, j) => ({ ...mm, directeur_publication: e.target.checked ? j === i : false })) })}
                  className="h-4 w-4 accent-[#B83227]"
                />
                Directeur de la publication (son nom apparaît dans les mentions légales)
              </label>
            </div>
          ))}
          <button
            type="button"
            data-testid="bureau-add-button"
            onClick={() => setForm({ ...form, bureau: [...bureau, { role: "", name: "" }] })}
            className="inline-flex items-center gap-1.5 rounded border border-stone-300 px-4 py-2 text-xs font-bold uppercase text-stone-600 hover:border-stone-500"
          >
            <Plus className="h-3.5 w-3.5" /> Ajouter un membre
          </button>
        </div>

        <button type="submit" data-testid="assoc-save-button" disabled={saving} className="rounded bg-[#B83227] px-8 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#96281E] disabled:opacity-60">
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}
